import { useEffect, useState, useMemo } from "react";
import { fetchPlotData } from "../../../network/plotdata";
import { useZoomContext } from "./ZoomContext";

export default function useZoomableChart(
  recordingId: string,
  sensorId: string,
  xKey: string,
  yKey: Array<string>,
  initialPlotData?: any[]
) {
  const [plotData, setPlotData] = useState<any>(initialPlotData || []);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const {
    refAreaLeft,
    refAreaRight,
    startIndex,
    endIndex,
    timeRange,
    setRefAreaLeft,
    setRefAreaRight,
    setTimeRange,
    setStartIndex,
    setEndIndex,
    resetZoom: contextResetZoom,
    dataReady,
    setDataReady,
    isLoading: contextIsLoading,
    setIsLoading: setContextIsLoading
  } = useZoomContext();

  // Only fetch data if initialPlotData is not provided
  useEffect(() => {
    if (!initialPlotData) {
      setIsLoading(true);
      const fetchAndSetData = async () => {
        try {
          const updatedData = await fetchData();
          setPlotData(updatedData);
          setIsLoading(false);
        } catch (error) {
          console.error(error);
          setIsLoading(false);
        }
      };

      fetchAndSetData();
    }
  }, [recordingId, xKey, yKey]); // Remove timeRange dependency to prevent loops
  
  // When initialPlotData changes and we have active zoom settings,
  // recalculate the correct start and end indices
  useEffect(() => {
    if (initialPlotData && timeRange.start && timeRange.end && dataReady) {
      // Find the indexes for the current time range in the updated data
      const startIdx = initialPlotData.findIndex((e: any) => e[xKey] === timeRange.start);
      const endIdx = initialPlotData.findIndex((e: any) => e[xKey] === timeRange.end);
      
      // If we found valid indexes, update them in the context
      if (startIdx !== -1) setStartIndex(startIdx);
      if (endIdx !== -1) setEndIndex(endIdx);
      
      // Reset the dataReady flag
      setDataReady(false);
      
      console.log(`Updated zoom indexes: ${startIdx} to ${endIdx} for time range ${timeRange.start} to ${timeRange.end}`);
    }
  }, [initialPlotData, timeRange.start, timeRange.end, xKey, setStartIndex, setEndIndex, dataReady, setDataReady]);

  async function fetchData() {
    const promises = yKey.map(
      async (key) => await fetchPlotData(recordingId, key, timeRange.start, timeRange.end, sensorId, undefined)
    );

    const results = await Promise.all(promises);

    let updatedData = [...(initialPlotData || plotData)];
    results.forEach((result) => {
      if (result.status === 200) {
        updatedData = addFetchedDataToPlotDataArray(result.data, updatedData);
      }
    });

    updatedData.sort((a: any, b: any) => a[xKey].localeCompare(b[xKey]));
    return updatedData;
  }

  function addFetchedDataToPlotDataArray(data: any, currentData: any[]) {
    const newData = [...currentData];
    data.forEach((entry: any) => {
      const index = newData.findIndex((e: any) => e[xKey] === entry[xKey]);
      if (index === -1) {
        newData.push(entry);
      } else {
        newData[index] = { ...newData[index], ...entry };
      }
    });
    return newData;
  }

  function handleMouseDown(e: any) {
    if (e) {
      setRefAreaLeft(e.activeLabel);
    }
  }

  function handleMouseMove(e: any) {
    if (e) {
      refAreaLeft && setRefAreaRight(e.activeLabel);
    }
  }

  async function zoom() {
    if (!refAreaLeft || !refAreaRight || refAreaLeft === refAreaRight) {
      setRefAreaLeft(undefined);
      setRefAreaRight(undefined);
      return;
    }

    // Set both local and shared loading states
    setIsLoading(true);
    setContextIsLoading(true);
    const sortedRefs = [refAreaLeft, refAreaRight].sort();
    const [newRefAreaLeft, newRefAreaRight] = sortedRefs;

    // Find the indexes for the zoomed area in the current data
    const dataToSearch = initialPlotData || plotData;
    const startIdx = dataToSearch.findIndex((e: any) => e[xKey] === newRefAreaLeft);
    const endIdx = dataToSearch.findIndex((e: any) => e[xKey] === newRefAreaRight);
    
    // Update the indexes in the context
    if (startIdx !== -1) setStartIndex(startIdx);
    if (endIdx !== -1) setEndIndex(endIdx);
    
    // Always mark timeRange updates from the zoom function as user actions
    setTimeRange({ start: newRefAreaLeft, end: newRefAreaRight }, true);
    
    // If we're not using shared data, handle our own data fetching
    if (!initialPlotData) {
      // Only fetch new data if we're not using shared data
      const promises = yKey.map(
        async (key) => await fetchPlotData(recordingId, key, newRefAreaLeft, newRefAreaRight, sensorId, undefined)
      );

      const results = await Promise.all(promises);
      
      // Add the new data to the existing data
      let currentData = [...plotData];
      results.forEach((result) => {
        if (result.status === 200) {
          currentData = addFetchedDataToPlotDataArray(result.data, currentData);
        }
      });
      
      // Sort the data by timestamp
      currentData.sort((a: any, b: any) => a[xKey].localeCompare(b[xKey]));
      
      // Update local state
      setPlotData(currentData);
    }
    
    setRefAreaLeft(undefined);
    setRefAreaRight(undefined);
    
    // Only clear local loading state here
    // The shared loading state will be cleared after data is fetched in GraphsContainer
    setIsLoading(false);
  }

  function resetZoom() {
    // Mark reset zoom as a user action too
    contextResetZoom(); // This already marks as user action in ZoomContext
  }

  // Improved data slicing logic with useMemo for performance
  const dataToDisplay = useMemo(() => {
    const source = initialPlotData || plotData;
    
    // No data or no zoom range
    if (!source || source.length === 0 || startIndex < 0 || endIndex < 0) {
      return source;
    }
    
    // Make sure we don't exceed array bounds
    const safeStart = Math.max(0, startIndex);
    const safeEnd = endIndex === Infinity ? undefined : Math.min(endIndex, source.length);
    
    return source.slice(safeStart, safeEnd);
  }, [initialPlotData, plotData, startIndex, endIndex]);

  // Combine local and context loading states
  const combinedIsLoading = isLoading || contextIsLoading;

  return {
    plotData: dataToDisplay,
    handleMouseDown,
    handleMouseMove,
    zoom,
    resetZoom,
    refAreaLeft,
    refAreaRight,
    isLoading: combinedIsLoading,
  };
}
